import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { isPast } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'

import { appointmentStatuses } from '../../../common/constants/appConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useGetAppointmentsQuery } from '../../../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { useClientsData } from '../../../lyraTherapy/clients/data/hooks/useClientsData'
import { useGetClientsCountsQuery } from '../../../lyraTherapy/clients/data/ltClientsApi'
import { ClientActivityItem } from '../panels/ClientActivityPanel'

const EXCLUDED_STATUSES = [appointmentStatuses.canceled, appointmentStatuses.missed]
const COUNTS_EXCLUDED_CONTENT_GROUPS = ['lesson', 'infosheet']
const NEXT_SESSION_LOOKAHEAD_MONTHS = 3

export const useClientActivity: () => {
  items: ClientActivityItem[]
  isLoading: boolean
} = () => {
  const userId = useSelector(getAuthUserId)

  const { data: activeClients, isLoading: isLoadingActiveClients } = useClientsData()

  const { data: clientsCounts, isLoading: isLoadingCounts } = useGetClientsCountsQuery({
    providerId: userId,
    excludeContentGroups: COUNTS_EXCLUDED_CONTENT_GROUPS,
  })

  const { data: upcomingAppointments, isLoading: isLoadingAppointments } = useGetAppointmentsQuery({
    providerId: userId,
    durationRange: { after: { months: NEXT_SESSION_LOOKAHEAD_MONTHS } },
    view: CalendarAppointmentsView.CALENDAR,
  })

  const nextSessionByClientId = useMemo(() => {
    const map = new Map<string, string>()
    const upcoming = (upcomingAppointments ?? [])
      .filter((appt) => !isPast(getDateFromAppointment(appt)) && !EXCLUDED_STATUSES.includes(appt.appointmentStatus))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    for (const appt of upcoming) {
      if (!map.has(appt.userId)) {
        map.set(appt.userId, getDateFromAppointment(appt).toISOString())
      }
    }
    return map
  }, [upcomingAppointments])

  const items = useMemo<ClientActivityItem[]>(() => {
    if (!activeClients || !clientsCounts) {
      return []
    }
    return activeClients
      .filter((client) => client.id in clientsCounts)
      .map((client) => {
        const unreviewedCount = clientsCounts[client.id].reduce((sum, c) => sum + (c.unread_responses ?? 0), 0)
        return { client, unreviewedCount, date: nextSessionByClientId.get(client.id) ?? null }
      })
      .filter((item) => item.unreviewedCount > 0)
  }, [activeClients, clientsCounts, nextSessionByClientId])

  return {
    items,
    isLoading: isLoadingActiveClients || isLoadingCounts || isLoadingAppointments,
  }
}
